import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import matter from 'gray-matter';
import Card from './Card';
import Button from './Button';
import Loading from './Loading';
import Alert from './Alert';
import EmptyState from './EmptyState';
import api from '../services/api';

// gray-matter references the Node `Buffer` global to stash an internal,
// unused `file.orig` snapshot — CRA 5 / webpack 5 no longer polyfills node
// globals for the browser, so without this shim `Buffer` is undefined at
// runtime and gray-matter throws on every parse. This stubs only the two
// members gray-matter's parse path touches (`Buffer.from`, `Buffer.isBuffer`)
// — it is not a general Buffer polyfill and must not be relied on elsewhere.
if (typeof window !== 'undefined' && typeof window.Buffer === 'undefined') {
  window.Buffer = {
    from: (input) => input,
    isBuffer: () => false,
  };
}

// Only these URL schemes are allowed to render as clickable links/images in
// the rendered Markdown. Everything else (javascript:, data:, vbscript:,
// etc.) is neutralized so the skill content — which is operator-editable,
// untrusted text stored server-side — can never execute script via a
// crafted link.
const ALLOWED_URL_SCHEMES = ['http:', 'https:', 'mailto:'];

function safeUrlTransform(url) {
  if (!url) return '';
  try {
    // Resolve relative URLs (e.g. "#section", "/path") against the current
    // origin so they're still judged by scheme, and pass through unchanged
    // when allowed.
    const parsed = new URL(url, window.location.origin);
    if (ALLOWED_URL_SCHEMES.includes(parsed.protocol)) {
      return url;
    }
  } catch {
    // Unparseable URL — treat as unsafe.
  }
  return '';
}

/** Pull the raw skill content string out of whatever shape the (opaque,
 * pass-through) backend response takes. The backend proxies straight to the
 * external healing agent with no response schema, so this is defensive
 * rather than assuming one exact field name. */
function extractRawContent(resp) {
  if (typeof resp === 'string') return resp;
  if (resp && typeof resp.content === 'string') return resp.content;
  return '';
}

/** js-yaml (which gray-matter uses to parse frontmatter) parses an
 * unquoted date-only value like `2026-08-20` as a `Date` at UTC midnight,
 * not a string — so a plain `String(value)` would render it as a verbose,
 * timezone-shifted date/time instead of the `YYYY-MM-DD` the server wrote.
 * Round-trip Dates back to that exact format instead. */
function formatMetaValue(value) {
  if (value == null) return '—';
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value);
}

function FrontmatterMeta({ data }) {
  const entries = Object.entries(data || {});
  if (entries.length === 0) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 3,
        mb: 3,
        pb: 2,
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      {entries.map(([key, value]) => (
        <Box key={key} sx={{ minWidth: 120 }}>
          <Typography sx={{ color: '#64748b', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {key}
          </Typography>
          <Typography sx={{ color: '#e2e8f0', fontSize: '0.85rem', mt: 0.25, wordBreak: 'break-word' }}>
            {formatMetaValue(value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

const markdownSx = {
  color: '#e2e8f0',
  fontSize: '0.9rem',
  lineHeight: 1.7,
  '& h1, & h2, & h3, & h4': { color: '#f1f5f9', fontWeight: 700, mt: 3, mb: 1.5 },
  '& h1': { fontSize: '1.4rem' },
  '& h2': { fontSize: '1.2rem' },
  '& h3': { fontSize: '1.05rem' },
  '& p': { mb: 1.5 },
  '& ul, & ol': { mb: 1.5, pl: 3 },
  '& li': { mb: 0.5 },
  '& a': { color: '#60a5fa' },
  '& code': {
    background: 'rgba(255,255,255,0.06)',
    borderRadius: '4px',
    padding: '2px 5px',
    fontSize: '0.85em',
    fontFamily: 'monospace',
  },
  '& pre': {
    background: 'rgba(0,0,0,0.35)',
    borderRadius: '8px',
    padding: '12px 14px',
    overflow: 'auto',
    mb: 1.5,
  },
  '& pre code': { background: 'none', padding: 0 },
  '& blockquote': {
    borderLeft: '3px solid rgba(255,255,255,0.15)',
    pl: 2,
    ml: 0,
    color: '#94a3b8',
  },
  '& table': { borderCollapse: 'collapse', mb: 1.5 },
  '& th, & td': {
    border: '1px solid rgba(255,255,255,0.12)',
    padding: '6px 10px',
    fontSize: '0.85rem',
  },
  '& img': { maxWidth: '100%' },
};

export default function SkillEditorPage() {
  const { retailer, attribute } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Carried from SkillsListPage's row click so a 'search' pageType skill
  // isn't silently read/written as the default 'detail' skill.
  const pageType = searchParams.get('pageType') || 'detail';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notOnboarded, setNotOnboarded] = useState(false);
  const [skillStatus, setSkillStatus] = useState(null);
  const [configFilePath, setConfigFilePath] = useState('');

  // rawContent is the full raw skill content (frontmatter + body) as last
  // confirmed by the server — the source of truth for view mode and for
  // resetting/discarding edits.
  const [rawContent, setRawContent] = useState('');
  const [deleteError, setDeleteError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchSkill = useCallback(async ({ silent } = {}) => {
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    setNotOnboarded(false);
    try {
      const resp = await api.getSkill(retailer, attribute, pageType);
      setSkillStatus(resp?.status || null);
      setConfigFilePath(resp?.configFilePath || resp?.config_file_path || '');
      if (resp?.status === 'pending') {
        setRawContent('');
        return;
      }
      const raw = extractRawContent(resp);
      if (!raw && resp?.status !== 'error') {
        setError('Skill response contained no content');
        return;
      }
      setRawContent(raw);
    } catch (err) {
      if (silent) return;
      if (err && err.body && err.body.code === 'not_onboarded') {
        setNotOnboarded(true);
      } else {
        setError(err.message || 'Failed to load skill');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [retailer, attribute, pageType]);

  useEffect(() => {
    fetchSkill();
  }, [fetchSkill]);

  useEffect(() => {
    if (skillStatus !== 'pending') return undefined;
    const intervalId = setInterval(() => {
      fetchSkill({ silent: true });
    }, 3000);
    return () => clearInterval(intervalId);
  }, [skillStatus, fetchSkill]);

  const parsed = useMemo(() => {
    try {
      return matter(rawContent || '', {
        // Skill content is operator-editable, untrusted text rendered for
        // every other viewer. gray-matter's default engine set includes a
        // `js`/`javascript` frontmatter language that runs the block through
        // eval() — disable it (and stub `json` to the safe JSON.parse) so
        // only plain YAML frontmatter is ever parsed.
        engines: { javascript: () => ({}), js: () => ({}), json: JSON.parse },
      });
    } catch {
      return { data: {}, content: rawContent || '' };
    }
  }, [rawContent]);

  function handleBack() {
    navigate(-1);
  }

  async function handleDelete() {
    if (!window.confirm(`Delete the skill for ${retailer} / ${attribute}? This cannot be undone.`)) {
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteSkill(retailer, attribute, pageType);
      navigate('/skills');
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete skill');
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return <Loading message="Loading skill..." />;
  }

  if (notOnboarded) {
    return (
      <EmptyState
        message="This attribute isn't onboarded yet"
        description={`No skill has been onboarded for ${retailer} / ${attribute}. Onboard it first before it can be viewed or edited.`}
      />
    );
  }

  if (skillStatus === 'pending') {
    return (
      <EmptyState
        message="Skill is still generating"
        description={`${retailer} / ${attribute} was queued. This page will refresh when generation finishes.`}
      />
    );
  }

  if (skillStatus === 'error' && !rawContent) {
    return (
      <EmptyState
        message="Skill generation failed"
        description={`${retailer} / ${attribute} could not be generated. Delete it and onboard again to retry.`}
      />
    );
  }

  if (error) {
    return <Alert variant="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography sx={{ color: '#e2e8f0', fontSize: '1.1rem', fontWeight: 700 }}>
            {retailer} / {attribute}
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: '0.8rem', mt: 0.25 }}>
            {pageType}
          </Typography>
          {configFilePath ? (
            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                mt: 0.5,
                wordBreak: 'break-all',
              }}
            >
              {configFilePath}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button variant="outlined" size="small" onClick={handleBack}>
            Back
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </Box>
      </Box>

      <Card>
        {deleteError && <Alert variant="error">{deleteError}</Alert>}
        <FrontmatterMeta data={parsed.data} />
        <Box sx={markdownSx}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} urlTransform={safeUrlTransform}>
            {parsed.content}
          </ReactMarkdown>
        </Box>
      </Card>
    </Box>
  );
}
